import React, { useEffect, useState } from 'react';
import { Download, X, Share, PlusSquare } from 'lucide-react';
import { getConsent, trackGA4Event } from '../lib/gtag';
import usePWAInstall, { isMobileDevice } from '../hooks/usePWAInstall';

/**
 * Verzögerter PWA-Install-Prompt (Bottom-Right-Banner)
 * - Wartet auf Cookie-Consent
 * - Erscheint 8-12 s nach Consent
 * - 14-Tage-Dismiss-Cooldown
 * - Nutzt den globalen usePWAInstall-Hook (shared mit PWAInstallButton)
 */
const STORAGE_KEY = 'fuelx-pwa-prompt-state';
const DISMISS_DAYS = 14;

function readState() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; }
}
function writeState(patch) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readState(), ...patch })); } catch { /* noop */ }
}

export default function PWAInstallPrompt() {
  const { canInstall, installed, isIOSSafari, promptInstall } = usePWAInstall();
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (installed) return;
    // Mobil kein Auto-Popup — Install-Buttons in Header/Footer reichen
    if (isMobileDevice()) return;

    const state = readState();
    if (state.installed) return;
    if (state.dismissedAt) {
      const daysAgo = (Date.now() - state.dismissedAt) / 86400000;
      if (daysAgo < DISMISS_DAYS) return;
    }

    // Warte auf Cookie-Consent
    let timer;
    const waitCookie = () => {
      if (getConsent()) return true;
      return false;
    };

    const scheduleShow = () => {
      // Android/Chrome: nach 8 s
      if (canInstall) {
        timer = setTimeout(() => {
          setVisible(true);
          trackGA4Event('pwa_prompt_shown', { variant: 'banner', platform: 'android' });
        }, 8000);
      } else if (isIOSSafari) {
        timer = setTimeout(() => {
          setIosHint(true);
          trackGA4Event('pwa_prompt_shown', { variant: 'banner', platform: 'ios' });
        }, 12000);
      }
    };

    if (waitCookie()) {
      scheduleShow();
    } else {
      const interval = setInterval(() => {
        if (getConsent()) {
          clearInterval(interval);
          scheduleShow();
        }
      }, 800);
      // Cleanup nach 3 min
      const cleanup = setTimeout(() => clearInterval(interval), 180000);
      return () => { clearInterval(interval); clearTimeout(cleanup); if (timer) clearTimeout(timer); };
    }

    return () => { if (timer) clearTimeout(timer); };
  }, [canInstall, installed, isIOSSafari]);

  const handleInstall = async () => {
    trackGA4Event('pwa_install_click', { variant: 'banner' });
    const ok = await promptInstall();
    if (ok) {
      writeState({ installed: true });
      trackGA4Event('pwa_install_accepted', { variant: 'banner' });
    } else {
      writeState({ dismissedAt: Date.now() });
      trackGA4Event('pwa_install_declined', { variant: 'banner' });
    }
    setVisible(false);
  };

  const handleDismiss = () => {
    writeState({ dismissedAt: Date.now() });
    trackGA4Event('pwa_install_dismissed', { variant: iosHint ? 'ios_hint' : 'banner' });
    setVisible(false);
    setIosHint(false);
  };

  if (installed) return null;
  if (!visible && !iosHint) return null;

  return (
    <div
      className="fixed left-3 right-3 bottom-3 sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm z-[70] animate-in slide-in-from-bottom-4 fade-in duration-500"
      data-testid="pwa-install-prompt"
    >
      <div className="relative rounded-2xl bg-stone-950 border border-emerald-400/25 shadow-2xl shadow-black/40 p-4 pr-10 backdrop-blur-lg">
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full text-stone-400 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors"
          aria-label="Schließen"
          data-testid="pwa-install-dismiss"
        >
          <X className="w-3.5 h-3.5" strokeWidth={2.4} />
        </button>

        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-emerald-400/10 border border-emerald-400/30 flex items-center justify-center flex-shrink-0">
            <img src="/apple-touch-icon.png" alt="FuelX" className="w-8 h-8 rounded-md" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-300 mb-1">
              FUEL.X App
            </p>
            {iosHint ? (
              <>
                <p className="text-sm font-semibold text-white leading-tight">
                  App zum Home-Bildschirm
                </p>
                <p className="text-[12px] text-stone-400 mt-1.5 leading-snug">
                  Tippe unten auf <Share className="inline w-3.5 h-3.5 -mt-0.5 text-stone-300" /> und wähle <span className="text-white font-medium">"Zum Home-Bildschirm hinzufügen"</span> <PlusSquare className="inline w-3.5 h-3.5 -mt-0.5 text-stone-300" />.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold text-white leading-tight">
                  FuelX-Card immer griffbereit
                </p>
                <p className="text-[12px] text-stone-400 mt-1.5 leading-snug">
                  Installiere FUEL.X als App — 1-Tap zum Konfigurator, Buchungen und Wallet.
                </p>
                <div className="flex gap-2 mt-3">
                  <button
                    type="button"
                    onClick={handleInstall}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-emerald-400 text-stone-950 text-[12px] font-semibold hover:bg-emerald-300 transition-colors"
                    data-testid="pwa-install-accept"
                  >
                    <Download className="w-3.5 h-3.5" strokeWidth={2.4} />
                    Installieren
                  </button>
                  <button
                    type="button"
                    onClick={handleDismiss}
                    className="inline-flex items-center px-3 py-2 rounded-full bg-white/5 border border-white/10 text-stone-300 text-[12px] hover:bg-white/10 transition-colors"
                  >
                    Später
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
